import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, UserCircle } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, formatApiError, fmtDate } from "@/lib/api";

const ROLES = [
  { v: "owner", l: "Owner" },
  { v: "branch_manager", l: "Branch Manager" },
  { v: "advisor", l: "Service Advisor" },
  { v: "mechanic", l: "Mechanic" },
  { v: "storekeeper", l: "Storekeeper" },
  { v: "cashier", l: "Cashier" },
  { v: "accountant", l: "Accountant" },
  { v: "driver", l: "Driver" },
];

export default function Users() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "mechanic" });

  const load = () => api.get("/users").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", form);
      toast.success("Team member added");
      setOpen(false);
      setForm({ name: "", email: "", phone: "", password: "", role: "mechanic" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="users-root">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase font-mono tracking-widest text-strawberry mb-1">People</div>
          <h1 className="text-3xl font-display font-bold">Team</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="users-add-btn" className="h-11 btn-primary-brand rounded-md text-white font-semibold">
              <Plus size={16} className="mr-1" /> Add team member
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none">
            <DialogHeader><DialogTitle className="font-display">Add team member</DialogTitle></DialogHeader>
            <form onSubmit={save} className="space-y-3" data-testid="user-form">
              <div><Label>Name</Label><Input required className="rounded-none mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email</Label><Input required type="email" className="rounded-none mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Phone</Label><Input className="rounded-none mt-1" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              </div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="rounded-none h-11 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Temporary password (min 6)</Label><Input required minLength={6} type="password" className="rounded-none mt-1" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div className="rounded-md bg-[#F1FAEE] border border-[#a8dadc]/60 px-3 py-2 text-[11.5px] text-[#1d3557]" data-testid="users-invite-hint">
                An invitation email with these credentials will be sent to the new team member. Ask them to change the password after their first sign-in.
              </div>
              <Button type="submit" data-testid="user-save-btn" className="w-full h-11 btn-primary-brand rounded-md text-white font-semibold">
                Add team member
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white dark:bg-white border border-frosted/50 dark:border-frosted/50">
        <table className="w-full table-industrial">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Added</th></tr></thead>
          <tbody>
            {items.map((u) => (
              <tr key={u.id}>
                <td className="flex items-center gap-2"><UserCircle size={18} className="text-steel/80" />{u.name}</td>
                <td className="text-sm text-steel">{u.email}</td>
                <td>
                  <span className="inline-block text-[10px] uppercase font-mono tracking-widest px-2 py-1 border bg-strawberry/10 text-strawberry border-strawberry/30">
                    {u.role}
                  </span>
                </td>
                <td>
                  <span className={`text-xs font-mono ${u.active ? "text-emerald-500" : "text-red-500"}`}>
                    {u.active ? "● active" : "● inactive"}
                  </span>
                </td>
                <td className="font-mono text-xs text-steel">{fmtDate(u.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
